import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  overflow: hidden;
`;

const BtnControls = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  top: 10px;
  left: 10px;
  z-index: 10;
`;
const Button = styled.button`
  padding: 8px 10px;
  background-color: #fff;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function ZoomImage({ image }) {
    const [transformState, setTransformState] = useState({
        scale: 1,
        translate: { x: 0, y: 0 }
    });
    const imageRef = useRef(null);
    const panning = useRef(false);
    const startPoint = useRef({ x: 0, y: 0 });
    const currentTranslate = useRef({ x: 0, y: 0 });

    const applyTransform = useCallback(() => {
        const { scale, translate } = transformState;
        if (imageRef.current) {
            imageRef.current.style.transform = `translate(${translate.x}px, ${translate.y}px) scale(${scale})`;
        }
    }, [transformState]);

    useEffect(() => {
        const img = imageRef.current;
        if (!img) return;

        const handleMouseDown = (event) => {
            event.preventDefault();
            panning.current = true;
            img.style.cursor = 'grabbing';
            startPoint.current = { x: event.clientX, y: event.clientY };
            currentTranslate.current = { ...transformState.translate };
        };

        const handleMouseUp = () => {
            panning.current = false;
            img.style.cursor = 'grab';
        };

        const handleMouseMove = (event) => {
            event.preventDefault();
            if (!panning.current) return;

            const deltaX = event.clientX - startPoint.current.x;
            const deltaY = event.clientY - startPoint.current.y;

            setTransformState({
                ...transformState,
                translate: {
                    x: currentTranslate.current.x + deltaX,
                    y: currentTranslate.current.y + deltaY
                }
            });
        };

        img.addEventListener('mousedown', handleMouseDown);
        img.addEventListener('mouseup', handleMouseUp);
        img.addEventListener('mousemove', handleMouseMove);

        applyTransform();

        // eslint-disable-next-line consistent-return
        return () => {
            img.removeEventListener('mousedown', handleMouseDown);
            img.removeEventListener('mouseup', handleMouseUp);
            img.removeEventListener('mousemove', handleMouseMove);
        };
    }, [applyTransform, transformState]);

    const handleZoom = useCallback((zoomIn) => {
        setTransformState((prevState) => {
            const newScale = zoomIn ? Math.min(prevState.scale * 1.2, 3) : Math.max(prevState.scale * 0.8, 1);
            // Maintain the center of the view during zoom
            const containerRect = imageRef.current?.offsetParent?.getBoundingClientRect();
            const imageRect = imageRef.current?.getBoundingClientRect();

            if (!containerRect || !imageRect) {
                return { ...prevState, scale: newScale };
            }

            const centerX = containerRect.width / 2;
            const centerY = containerRect.height / 2;

            const scaledX = (centerX - prevState.translate.x) / prevState.scale;
            const scaledY = (centerY - prevState.translate.y) / prevState.scale;

            const newTranslateX = centerX - scaledX * newScale;
            const newTranslateY = centerY - scaledY * newScale;

            return { scale: newScale, translate: { x: newTranslateX, y: newTranslateY } };
        });
    }, []);

    const handleZoomIn = useCallback(() => handleZoom(true), [handleZoom]);
    const handleZoomOut = useCallback(() => handleZoom(false), [handleZoom]);

    return (
        <Container>
            <BtnControls>
                <Button onClick={handleZoomIn} disabled={transformState.scale === 3}>
                    <i className="fa-solid fa-plus" />
                </Button>
                <Button onClick={handleZoomOut} disabled={transformState.scale === 1}>
                    <i className="fa-solid fa-minus" />
                </Button>
            </BtnControls>
            <img
                ref={imageRef}
                src={image}
                alt={`attachment-${image}`}
                style={{
                    cursor: 'grab',
                    transformOrigin: 'center center',
                    width: '100%',
                    height: '100%'
                }}
            />
        </Container>
    );
}

export default ZoomImage;
